import { useState } from 'react';
import {
  Users,
  Download,
  XCircle,
  CheckCircle2,
  MoreVertical,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils/utils';
import { SignalAuthorDisplay } from '../shared/components/SignalAuthorDisplay';

interface MarketplaceCardProps {
  title: string;
  rank: number;
  userCount: number;
  isImported: boolean;
  owner?: {
    fullName: string;
    imageUrl?: string;
  };
  onCardClick: () => void;
  onImport: () => void;
  onRemove: () => void;
  isImporting?: boolean;
  isRemoving?: boolean;
}

export const MarketplaceCard = ({
  title,
  rank,
  userCount,
  isImported,
  owner,
  onCardClick,
  onImport,
  onRemove,
  isImporting,
  isRemoving,
}: MarketplaceCardProps) => {
  const { t } = useTranslation('knowledge');
  const [menuOpen, setMenuOpen] = useState(false);
  const isTopRated = rank < 2;

  return (
    <div
      className={cn(
        'relative flex min-h-[230px] cursor-pointer flex-col rounded-[14px] border border-[#ece8e1] bg-[#fffffe] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.05)] transition-all duration-300 hover:-translate-y-0.5 hover:z-10 hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)]',
        isTopRated && 'border-2 border-amber-400'
      )}
      onClick={onCardClick}
    >
      {/* Top Rated flag */}
      {isTopRated && (
        <div className="absolute start-4 top-0">
          <div className="rounded-b-md bg-amber-400 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-900">
            {t('marketplace.topRated')}
          </div>
        </div>
      )}

      {/* Title */}
      <h3
        className={cn(
          'line-clamp-3 h-[72px] text-[14.5px] font-medium italic leading-[1.65] text-[#2c2520]',
          isTopRated && 'mt-4'
        )}
      >
        {title}
      </h3>

      <div className="mt-auto">
        {/* Users count */}
        <div className="flex items-center justify-end gap-1 pb-3 text-[11px] text-[#8c8078]">
          <Users className="h-3.5 w-3.5" />
          <span>{userCount}</span>
        </div>

        {/* Separator */}
        <div className="border-t border-[#f0ece6]" />

        {/* Footer */}
        <div className="flex items-center justify-between pt-3">
          {/* Author */}
          <SignalAuthorDisplay owner={owner} size="md" />

          {/* Import / Imported + menu */}
          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {isImported ? (
              <>
                <div className="flex items-center gap-1.5 text-sm font-medium text-green-600">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{t('marketplace.imported')}</span>
                </div>
                <Popover open={menuOpen} onOpenChange={setMenuOpen}>
                  <PopoverTrigger asChild>
                    <button
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      aria-label="More options"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-40 p-1">
                    <button
                      disabled={isRemoving}
                      className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:pointer-events-none disabled:opacity-50"
                      onClick={() => {
                        setMenuOpen(false);
                        onRemove();
                      }}
                    >
                      <XCircle className="h-4 w-4" />
                      {t('marketplace.remove')}
                    </button>
                  </PopoverContent>
                </Popover>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={onImport}
                disabled={isImporting}
              >
                <Download className="h-4 w-4" />
                {t('marketplace.import')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
